import { z } from 'zod';

import { schoolSchema } from './school-shared.js';
import { paginatedResponse } from './util-schema-types.js';

import { Temporal } from 'temporal-polyfill';
import { DEPRECATED_pagingSchemaWithMaxPageSize, filteringSchemaFrom } from './modifier.js';
import {
  minimalOrgSchema,
  orgFeedbackTypeSchema,
  orgSchema,
  orgStatusSchema,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
} from './org-shared.js';
import { userRoleSchema } from './user-role.js';
import { studentSchema } from './student.js';
import { userSchema } from './user.js';
import { backgroundCheckStatusSchema, techCheckTypeSchema } from './coach-shared.js';

export { OrgStatus, VideoConferenceType } from './org-shared.js';
export type { MinimalOrg, Org } from './org-shared.js';
export { minimalOrgSchema, orgSchema, orgStatusSchema, VideoConferenceTypeLabels, videoConferenceTypeSchema };

export const orgWithSchoolsSchema = orgSchema.extend({
  schools: z.array(schoolSchema),
});
export type OrgWithSchools = z.infer<typeof orgWithSchoolsSchema>;

export const schoolWithUserRolesSchema = schoolSchema.merge(
  z.object({
    user_roles: z.array(
      userRoleSchema.merge(
        z.object({ user: z.lazy(() => userSchema.merge(z.object({ studentProfile: studentSchema.nullable() }))) }),
      ),
    ),
  }),
);

export const orgWithSchoolsAndStudentProfilesSchema = orgSchema.extend({
  schools: z.array(schoolWithUserRolesSchema),
});

export type OrgWithSchoolsAndStudentProfiles = z.infer<typeof orgWithSchoolsAndStudentProfilesSchema>;

export const orgWithSchoolsAndStatsSchema = orgSchema.extend({
  schools: z.array(schoolSchema),
  totalEnrolledStudents: z.number().optional(),
  startPlainDate: z.custom<Temporal.PlainDate>().optional(),
  endPlainDate: z.custom<Temporal.PlainDate>().optional(),
});
export type OrgWithSchoolsAndStats = z.infer<typeof orgWithSchoolsAndStatsSchema>;

export const orgWithSchoolsAndStartEndDateSchema = orgWithSchoolsSchema.extend({
  startPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  endPlainDate: z.instanceof(Temporal.PlainDate).optional(),
});
export type OrgWithSchoolsAndStartEndDate = z.infer<typeof orgWithSchoolsAndStartEndDateSchema>;

export const orgWithSchoolsEdlinkIdentifiableSchema = orgWithSchoolsSchema.extend({
  edlinkId: z.string().nullable(),
  schools: z.array(
    schoolWithUserRolesSchema.extend({
      edlinkId: z.string().nullable(),
    }),
  ),
});
export type OrgWithSchoolsEdlinkIdentifiable = z.infer<typeof orgWithSchoolsEdlinkIdentifiableSchema>;

export const getOrgsFilterSchema = z.object({
  query: z.string().optional(),
  meetingType: z.array(videoConferenceTypeSchema.optional()).optional(),
  hasEdlinkId: z.boolean().optional(),
});
export type GetOrgsFilter = z.infer<typeof getOrgsFilterSchema>;

export const getOrgsModifierSchema = DEPRECATED_pagingSchemaWithMaxPageSize.merge(filteringSchemaFrom(getOrgsFilterSchema));
export type GetOrgsModifier = z.infer<typeof getOrgsModifierSchema>;

export const getOrgsResultsSchema = paginatedResponse.extend({
  orgs: z.array(z.lazy(() => orgWithSchoolsAndStatsSchema)),
});
export type GetOrgsResults = z.infer<typeof getOrgsResultsSchema>;

export const getMinimalOrgsResultsSchema = paginatedResponse.extend({
  orgs: z.array(minimalOrgSchema),
});
export type GetMinimalOrgsResults = z.infer<typeof getMinimalOrgsResultsSchema>;

export const createOrgOptionsSchema = z.object({
  name: z.string(),
  status: orgStatusSchema.optional(),
  timeZone: z.string(),
  edlinkId: z.string().optional(),
  videoConferenceType: videoConferenceTypeSchema.optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  feedbackType: orgFeedbackTypeSchema.optional(),
  totalSeats: z.number().min(0).optional(),
  teacherBackgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  teacherTechCheckTypes: z.array(techCheckTypeSchema).optional(),
  v1Id: z.string().optional(),
});
export type CreateOrgOptions = z.infer<typeof createOrgOptionsSchema>;

export const updateOrgOptionsSchema = z.object({
  name: z.string().optional(),
  status: orgStatusSchema.optional(),
  timeZone: z.string().optional(),
  edlinkId: z.string().trim().min(1).nullable().optional(),
  integrationId: z.string().trim().min(1).nullable().optional(),
  videoConferenceType: videoConferenceTypeSchema.optional(),
  feedbackType: orgFeedbackTypeSchema.optional(),
  totalSeats: z.number().int().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  teacherBackgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  teacherTechCheckTypes: z.array(techCheckTypeSchema).optional(),
  canSendEmailSMS: z.boolean().optional(),
  showSessionsToTeachers: z.boolean().optional(),
  v1Id: z.string().optional(),
}).refine(
  (data) => {
    if (data.edlinkId == null) {
      return true;
    }
    return typeof data.integrationId === 'string' && data.integrationId.length > 0;
  },
  {
    message: 'Integration ID is required when setting an Edlink ID',
    path: ['integrationId'],
  },
);
export type UpdateOrgOptions = z.infer<typeof updateOrgOptionsSchema>;

export const OrganizationMatchNames = {
  JPS: 'Jefferson',
};
